import { ArrowDown, ArrowUp, Power, RotateCw } from "lucide-react";
import { Link } from "react-router";
import {
  useAdminInfra,
  useDynoAction,
  useResizeProcess,
} from "../../api/admin";
import { HerokuDyno, HerokuFormation } from "../../types/admin";
import { formatDateTime } from "../../utils/dateUtils";
import { notify } from "./notify";
import { Badge, Button, Card, Empty, Loading, PageHeader } from "./ui";

const STATE_COLORS: Record<string, "green" | "yellow" | "red" | "gray"> = {
  up: "green",
  starting: "yellow",
  restarting: "yellow",
  crashed: "red",
  down: "gray",
};

/** One-off PDF dynos run `rake books:generate_pdf BOOK_ID=<id>`. */
const pdfBookId = (command: string) =>
  command.match(/books:generate_pdf BOOK_ID=(\d+)/)?.[1];

export const AdminInfra = () => {
  const { data, isLoading } = useAdminInfra();

  if (isLoading || !data) return <Loading />;

  if (!data.enabled) {
    return (
      <>
        <PageHeader title="Infra Heroku" />
        <Card>
          <Empty>
            Heroku n'est pas configuré : il faut HEROKU_APP_NAME et
            HEROKU_API_KEY dans les variables d'environnement.
          </Empty>
        </Card>
      </>
    );
  }

  const oneOffs = data.dynos.filter((d) => d.type === "run");
  const processDynos = data.dynos.filter((d) => d.type !== "run");

  return (
    <>
      <PageHeader
        title="Infra Heroku"
        subtitle={`App ${data.app_name} · les PDF tournent sur un dyno dédié ${data.pdf_size} (HEROKU_TARGET_DYNO_TYPE) · rafraîchi toutes les 15 s`}
      />

      <div className="space-y-6">
        <Card title="Process">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                <th className="px-5 py-3 font-medium">Process</th>
                <th className="px-5 py-3 font-medium">Commande</th>
                <th className="px-5 py-3 font-medium">Dynos</th>
                <th className="px-5 py-3 font-medium">Taille</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.formation
                .filter((f) => f.quantity > 0)
                .map((formation) => (
                  <FormationRow
                    key={formation.type}
                    formation={formation}
                    sizes={data.sizes}
                  />
                ))}
            </tbody>
          </table>
        </Card>

        <Card title={`Générations PDF en cours (${oneOffs.length})`}>
          {oneOffs.length === 0 ? (
            <Empty>Aucun dyno one-off en cours.</Empty>
          ) : (
            <DynoTable dynos={oneOffs} />
          )}
        </Card>

        <Card title="Dynos">
          <DynoTable dynos={processDynos} />
        </Card>
      </div>
    </>
  );
};

const FormationRow = ({
  formation,
  sizes,
}: {
  formation: HerokuFormation;
  sizes: string[];
}) => {
  const resize = useResizeProcess();
  const index = sizes.indexOf(formation.size);
  const smaller = index > 0 ? sizes[index - 1] : undefined;
  const bigger =
    index >= 0 && index < sizes.length - 1 ? sizes[index + 1] : undefined;

  const go = (size: string) =>
    resize.mutate(
      { process: formation.type, size },
      notify(`${formation.type} passe en ${size}`),
    );

  const warning = `Passer ${formation.type} en %s ? Ses dynos vont redémarrer : les jobs en cours sur ce process seront interrompus puis relancés.`;

  return (
    <tr className="align-middle">
      <td className="px-5 py-3 font-medium text-gray-900">{formation.type}</td>
      <td className="px-5 py-3 text-xs text-gray-500 font-mono">
        {formation.command}
      </td>
      <td className="px-5 py-3">{formation.quantity}</td>
      <td className="px-5 py-3">
        <Badge color="indigo">{formation.size}</Badge>
      </td>
      <td className="px-5 py-3">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => smaller && go(smaller)}
            disabled={!smaller || resize.isLoading}
            confirm={smaller && warning.replace("%s", smaller)}
          >
            <ArrowDown className="w-3.5 h-3.5" /> {smaller ?? "—"}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => bigger && go(bigger)}
            disabled={!bigger || resize.isLoading}
            confirm={bigger && warning.replace("%s", bigger)}
          >
            <ArrowUp className="w-3.5 h-3.5" /> {bigger ?? "—"}
          </Button>
        </div>
      </td>
    </tr>
  );
};

const DynoTable = ({ dynos }: { dynos: HerokuDyno[] }) => {
  const restart = useDynoAction("restart");
  const stop = useDynoAction("stop");

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
          <th className="px-5 py-3 font-medium">Dyno</th>
          <th className="px-5 py-3 font-medium">Taille</th>
          <th className="px-5 py-3 font-medium">État</th>
          <th className="px-5 py-3 font-medium">Depuis</th>
          <th className="px-5 py-3 font-medium"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {dynos.map((dyno) => {
          const bookId = pdfBookId(dyno.command);
          return (
            <tr key={dyno.name}>
              <td className="px-5 py-3">
                <p className="font-medium text-gray-900">{dyno.name}</p>
                {bookId ? (
                  <Link
                    to={`/admin/books/${bookId}`}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    PDF du livre #{bookId}
                  </Link>
                ) : (
                  <p className="text-xs text-gray-400 font-mono truncate max-w-xs">
                    {dyno.command}
                  </p>
                )}
              </td>
              <td className="px-5 py-3">{dyno.size}</td>
              <td className="px-5 py-3">
                <Badge color={STATE_COLORS[dyno.state] ?? "gray"}>
                  {dyno.state}
                </Badge>
              </td>
              <td className="px-5 py-3 text-xs text-gray-500">
                {formatDateTime(dyno.updated_at)}
              </td>
              <td className="px-5 py-3">
                <div className="flex justify-end">
                  {dyno.type === "run" ? (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        stop.mutate(dyno.name, notify(`${dyno.name} arrêté`))
                      }
                      disabled={stop.isLoading}
                      confirm={`Arrêter ${dyno.name} ? La génération en cours sera perdue.`}
                    >
                      <Power className="w-3.5 h-3.5" /> Arrêter
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() =>
                        restart.mutate(
                          dyno.name,
                          notify(`${dyno.name} redémarré`),
                        )
                      }
                      disabled={restart.isLoading}
                      confirm={`Redémarrer ${dyno.name} ?`}
                    >
                      <RotateCw className="w-3.5 h-3.5" /> Redémarrer
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
